import { useEffect, useRef, useState } from "react";
import { resolveAddressThunk } from "app/features";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectReportsState } from "app/data/selectors";

const useResolveAddress = (data: any) => {
  const dispatch = useAppDispatch();
  const { resolvedAddresses } = useAppSelector(selectReportsState);

  const hasResolvedAddresses = useRef(false);
  const processedDataKey = useRef<string>("");
  const [pendingRequests, setPendingRequests] = useState<Set<string>>(
    new Set()
  );
  const [resolvedAddressLoading, setResolvedAddressLoading] = useState(false);

  useEffect(() => {
    // Create a unique key for the current data to detect changes
    const currentDataKey = data?.length
      ? `${data.length}-${data.map((item: any) => item.uniqueId || item.id).join(",")}`
      : "";

    // Reset the flag if we have completely different data
    if (currentDataKey !== processedDataKey.current) {
      hasResolvedAddresses.current = false;
      processedDataKey.current = currentDataKey;
      setPendingRequests(new Set());
      setResolvedAddressLoading(false);
    }

    if (!hasResolvedAddresses.current && data?.length) {
      const uniqueCoordinates = new Set();
      const newPendingRequests = new Set<string>();

      data.forEach((row: any) => {
        const startKey = `${row.startLatitude},${row.startLongitude}`;
        const endKey = `${row.endLatitude},${row.endLongitude}`;

        if (
          row.startLatitude > 0 &&
          row.startLongitude > 0 &&
          !uniqueCoordinates.has(startKey)
        ) {
          uniqueCoordinates.add(startKey);
          const existingStartAddress = resolvedAddresses.find(
            (item) =>
              item.latitude === row.startLatitude &&
              item.longitude === row.startLongitude
          );

          if (!existingStartAddress) {
            newPendingRequests.add(startKey);

            // Dispatch the API call and track its completion
            dispatch(
              resolveAddressThunk({
                latitude: row.startLatitude,
                longitude: row.startLongitude
              })
            )
              .then(() => {
                // Remove this request from pending when it completes
                setPendingRequests((prev) => {
                  const updated = new Set(prev);
                  updated.delete(startKey);
                  return updated;
                });
              })
              .catch(() => {
                // Remove this request from pending even if it fails
                setPendingRequests((prev) => {
                  const updated = new Set(prev);
                  updated.delete(startKey);
                  return updated;
                });
              });
          }
        }

        if (
          row.endLatitude > 0 &&
          row.endLongitude > 0 &&
          !uniqueCoordinates.has(endKey)
        ) {
          uniqueCoordinates.add(endKey);
          const existingEndAddress = resolvedAddresses.find(
            (item) =>
              item.latitude === row.endLatitude &&
              item.longitude === row.endLongitude
          );

          if (!existingEndAddress) {
            newPendingRequests.add(endKey);

            // Dispatch the API call and track its completion
            dispatch(
              resolveAddressThunk({
                latitude: row.endLatitude,
                longitude: row.endLongitude
              })
            )
              .then(() => {
                // Remove this request from pending when it completes
                setPendingRequests((prev) => {
                  const updated = new Set(prev);
                  updated.delete(endKey);
                  return updated;
                });
              })
              .catch(() => {
                // Remove this request from pending even if it fails
                setPendingRequests((prev) => {
                  const updated = new Set(prev);
                  updated.delete(endKey);
                  return updated;
                });
              });
          }
        }
      });

      if (newPendingRequests.size > 0) {
        setPendingRequests(newPendingRequests);
        setResolvedAddressLoading(true);
        hasResolvedAddresses.current = true;
      }
    }
  }, [data, resolvedAddresses, dispatch]);

  // Update loading state based on pending requests
  useEffect(() => {
    if (pendingRequests.size === 0 && resolvedAddressLoading) {
      setResolvedAddressLoading(false);
    }
  }, [pendingRequests.size, resolvedAddressLoading]);

  return { resolvedAddresses, resolvedAddressLoading };
};

export default useResolveAddress;
